/**
 * Shared Recipe Page
 * Beautiful standalone recipe view with instant access
 * Subtle app benefits, no signup required
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Collapse
} from '@mui/material';
import {
  AccessTime,
  Restaurant,
  People,
  Star,
  Share,
  Print,
  GetApp,
  Add,
  ExpandMore,
  Close,
  ShoppingCart,
  Launch
} from '@mui/icons-material';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { shareRecipe } from '../utils/shareHelper';
import { exportRecipe } from '../utils/fileSystemHelper';
import { useAuth } from '../contexts/AuthContext';
import { getAmazonSearchUrl, AMAZON_DISCLOSURE } from '../utils/amazonHelper';

export default function SharedRecipePage() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAppBenefits, setShowAppBenefits] = useState(false);
  const [servings, setServings] = useState(null);

  useEffect(() => {
    loadRecipe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  const updateMetaTags = (recipeData) => {
    const recipeName = recipeData.name || recipeData.title || 'Recipe';
    const description = recipeData.description || `Check out this ${recipeName} recipe!`;
    const imageUrl = recipeData.photoUrl || recipeData.photoURL || 'https://earnechoreapp.web.app/logo512.png';
    const url = window.location.href;

    // Update document title
    document.title = `${recipeName} - Earn-E Recipes`;

    // Helper to update or create meta tags
    const setMetaTag = (property, content) => {
      let tag = document.querySelector(`meta[property="${property}"]`) || 
                document.querySelector(`meta[name="${property}"]`);
      
      if (!tag) {
        tag = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          tag.setAttribute('property', property);
        } else {
          tag.setAttribute('name', property);
        }
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // Open Graph tags (Facebook, LinkedIn, etc.)
    setMetaTag('og:title', recipeName);
    setMetaTag('og:description', description);
    setMetaTag('og:image', imageUrl);
    setMetaTag('og:url', url);
    setMetaTag('og:type', 'article');
    setMetaTag('og:site_name', 'Earn-E');

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', recipeName);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', imageUrl);

    // Standard meta tags
    setMetaTag('description', description);

    console.log('📝 Updated meta tags for:', recipeName);
  };

  const loadRecipe = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Loading recipe:', recipeId);
      
      // Try old collection first (most common for manual recipes)
      let recipeDoc = await getDoc(doc(db, 'familyRecipes', recipeId));
      console.log('📚 Checked familyRecipes:', recipeDoc.exists());
      
      // If not found in old collection, search the new subcollections
      // This is needed for Burt-created recipes
      if (!recipeDoc.exists()) {
        console.log('🔍 Searching families subcollections...');
        
        try {
          // Get all families and search their recipe subcollections
          const familiesSnapshot = await getDocs(collection(db, 'families'));
          console.log(`📂 Found ${familiesSnapshot.size} families to search`);
          
          for (const familyDoc of familiesSnapshot.docs) {
            console.log(`   🔎 Checking family: ${familyDoc.id}`);
            try {
              const subcollectionRecipe = await getDoc(
                doc(db, 'families', familyDoc.id, 'recipes', recipeId)
              );
              
              console.log(`      Recipe exists in ${familyDoc.id}:`, subcollectionRecipe.exists());
              
              if (subcollectionRecipe.exists()) {
                console.log(`✅ Recipe found in family ${familyDoc.id} subcollection!`);
                recipeDoc = subcollectionRecipe;
                break;
              }
            } catch (subError) {
              // This family might not have a recipes subcollection, continue searching
              console.log(`      ⚠️ Error checking family ${familyDoc.id}:`, subError.message);
            }
          }
          
          if (!recipeDoc.exists()) {
            console.log('❌ Recipe not found in any family subcollection');
          }
        } catch (familyError) {
          console.error('❌ Error accessing families collection:', familyError);
        }
      }
      
      if (recipeDoc.exists()) {
        const data = recipeDoc.data();
        console.log('✅ Recipe found:', data.name || data.title);
        setRecipe({ id: recipeDoc.id, ...data });
        setServings(data.servings || 4);
        
        // Update page meta tags for rich link previews
        updateMetaTags(data);
      } else {
        console.log('❌ Recipe not found in any collection');
        setError('Recipe not found. It may have been deleted.');
      }
    } catch (err) {
      console.error('❌ Error loading recipe:', err);
      setError(`Failed to load recipe: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    await shareRecipe(recipe);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async () => {
    await exportRecipe(recipe);
  };

  // Build a readable string from an ingredient object or plain string
  const ingredientToString = (ing) => {
    if (typeof ing === 'string') return ing;
    const parts = [];
    if (ing.amount) parts.push(ing.amount);
    if (ing.unit) parts.push(ing.unit);
    if (ing.name) parts.push(ing.name);
    return parts.join(' ').trim();
  };

  // Build Amazon search URL for the recipe — searches recipe name for meal kits,
  // ingredient bundles, and related products (works much better than dumping all ingredients)
  const getAllIngredientsAmazonUrl = () => {
    if (!recipe) return null;
    const recipeName = recipe.name || recipe.title || '';
    if (!recipeName) return null;
    return getAmazonSearchUrl(recipeName + ' ingredients');
  };

  const adjustIngredient = (amount, originalServings, newServings) => {
    // If amount is not a valid number, return it as-is
    if (!amount || !originalServings || !newServings) return amount || '';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return amount; // Return original if not a number
    
    const ratio = newServings / originalServings;
    const adjusted = numAmount * ratio;
    return adjusted % 1 === 0 ? adjusted : adjusted.toFixed(2);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !recipe) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Alert severity="error">
          {error || 'Recipe not found'}
        </Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
          Go Home
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 8 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
        className="no-print"
      >
        <Typography variant="h6">Earn-E Recipe</Typography>
        <Box display="flex" gap={1}>
          <IconButton color="inherit" onClick={handleShare} size="small">
            <Share />
          </IconButton>
          <IconButton color="inherit" onClick={handlePrint} size="small">
            <Print />
          </IconButton>
          <IconButton color="inherit" onClick={handleExport} size="small">
            <GetApp />
          </IconButton>
        </Box>
      </Box>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Grid container spacing={3}>
          {/* Main Recipe Content */}
          <Grid item xs={12} md={8}>
            <Card elevation={3}>
              {(recipe.photoUrl || recipe.photoURL) && (
                <CardMedia
                  component="img"
                  height="400"
                  image={recipe.photoUrl || recipe.photoURL}
                  alt={recipe.name || recipe.title}
                  sx={{ objectFit: 'cover' }}
                />
              )}

              <CardContent>
                {/* Title & Description */}
                <Typography variant="h3" gutterBottom fontWeight="bold">
                  {recipe.name || recipe.title}
                </Typography>

                {recipe.description && (
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {recipe.description}
                  </Typography>
                )}

                {/* Meta Info */}
                <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                  {recipe.prepTime && (
                    <Chip
                      icon={<AccessTime />}
                      label={`Prep: ${recipe.prepTime} min`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {recipe.cookTime && (
                    <Chip
                      icon={<AccessTime />}
                      label={`Cook: ${recipe.cookTime} min`}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {servings && (
                    <Chip
                      icon={<People />}
                      label={`Servings: ${servings}`}
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                  {recipe.difficulty && (
                    <Chip
                      icon={<Star />}
                      label={recipe.difficulty}
                      color="success"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Ingredients */}
                {recipe.ingredients && recipe.ingredients.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      📋 Ingredients
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {recipe.ingredients.map((ing, index) => {
                        // Handle both string and object formats
                        // Burt AI saves as strings like "2 cups flour"
                        // Manual recipes save as objects {amount, unit, name}
                        
                        if (typeof ing === 'string') {
                          // Plain string format (from Burt AI)
                          return (
                            <Typography component="li" key={index} variant="body1" sx={{ mb: 1 }}>
                              {ing}
                            </Typography>
                          );
                        }
                        
                        // Object format (from manual entry or structured Burt AI)
                        const hasAmount = ing.amount && ing.amount.trim();
                        const hasUnit = ing.unit && ing.unit.trim();
                        
                        // Only adjust amounts that are numeric
                        const adjustedAmount = hasAmount ? adjustIngredient(ing.amount, recipe.servings, servings) : '';
                        
                        // Check for special cases like "to taste"
                        const isToTaste = ing.note === 'to taste' || 
                                         ing.name.toLowerCase().includes('to taste') ||
                                         ing.name.toLowerCase().includes('as needed');
                        
                        return (
                          <Typography component="li" key={index} variant="body1" sx={{ mb: 1 }}>
                            {hasAmount && hasUnit && (
                              <strong>{adjustedAmount} {ing.unit} </strong>
                            )}
                            {hasAmount && !hasUnit && (
                              <strong>{adjustedAmount} </strong>
                            )}
                            {ing.name}
                            {(ing.notes || isToTaste) && !ing.name.toLowerCase().includes('to taste') && (
                              <Typography component="span" color="text.secondary" variant="body2">
                                {' '}({ing.notes || ing.note || 'to taste'})
                              </Typography>
                            )}
                          </Typography>
                        );
                      })}
                    </Box>
                  </Box>
                )}

                {/* Amazon Ingredients CTA — affiliate links for external users */}
                {recipe.ingredients && recipe.ingredients.length > 0 && (
                  <Box
                    sx={{
                      my: 3,
                      p: 2.5,
                      bgcolor: '#FFF8F0',
                      border: '1px solid #FF990055',
                      borderRadius: 2
                    }}
                    className="no-print"
                  >
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ShoppingCart sx={{ color: '#FF9900' }} />
                      Order ingredients online
                    </Typography>

                    {/* Per-ingredient Amazon links */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {recipe.ingredients.slice(0, 8).map((ing, index) => {
                        const label = ingredientToString(ing);
                        if (!label || label.length < 3) return null;
                        // Extract just the ingredient name (skip amount/unit) for search
                        const searchTerm = typeof ing === 'string'
                          ? ing.replace(/^[\d\s/½¼¾]+\s*(cups?|tbsp|tsp|lbs?|oz|pounds?|grams?|g|kg|ml|cloves?|can|cans|pkg|bunches?|heads?|stalks?|sprigs?)?\s*/i, '').trim() || ing
                          : ing.name || label;
                        return (
                          <Chip
                            key={index}
                            label={searchTerm.length > 28 ? searchTerm.slice(0, 28) + '…' : searchTerm}
                            component="a"
                            href={getAmazonSearchUrl(searchTerm)}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            icon={<Launch sx={{ fontSize: '0.75rem !important' }} />}
                            size="small"
                            sx={{
                              bgcolor: '#FF990022',
                              border: '1px solid #FF990066',
                              '&:hover': { bgcolor: '#FF990044' },
                              cursor: 'pointer'
                            }}
                          />
                        );
                      })}
                      {recipe.ingredients.length > 8 && (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                          +{recipe.ingredients.length - 8} more
                        </Typography>
                      )}
                    </Box>

                    {/* Bulk "shop all" button */}
                    <Button
                      variant="contained"
                      startIcon={<ShoppingCart />}
                      href={getAllIngredientsAmazonUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      sx={{ bgcolor: '#FF9900', color: '#000', '&:hover': { bgcolor: '#E68A00' }, fontWeight: 'bold' }}
                    >
                      Search "{recipe.name || recipe.title}" on Amazon
                    </Button>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                      {AMAZON_DISCLOSURE}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 3 }} />

                {/* Instructions */}
                {recipe.instructions && recipe.instructions.length > 0 && (
                  <Box mb={4}>
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      👨‍🍳 Instructions
                    </Typography>
                    {recipe.instructions.map((step, index) => (
                      <Box key={index} mb={2}>
                        <Typography variant="h6" color="primary">
                          Step {index + 1}
                        </Typography>
                        <Typography variant="body1">{step}</Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Tips */}
                {recipe.tips && recipe.tips.length > 0 && (
                  <Box>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                      💡 Tips
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {recipe.tips.map((tip, index) => (
                        <Typography component="li" key={index} variant="body1" sx={{ mb: 1 }}>
                          {tip}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                <Box display="flex" gap={2} mt={4} flexWrap="wrap" className="no-print">
                  <Button
                    variant="outlined"
                    startIcon={<Share />}
                    onClick={handleShare}
                  >
                    Share This Recipe
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<GetApp />}
                    onClick={handleExport}
                  >
                    Download
                  </Button>
                  {getAllIngredientsAmazonUrl() && (
                    <Button
                      variant="contained"
                      startIcon={<ShoppingCart />}
                      href={getAllIngredientsAmazonUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      component="a"
                      sx={{ bgcolor: '#FF9900', color: '#000', '&:hover': { bgcolor: '#E68A00' } }}
                    >
                      Shop Ingredients
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar - App Benefits (Subtle!) */}
          <Grid item xs={12} md={4} className="no-print">
            <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 16 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Restaurant color="primary" />
                  <Typography variant="h6">
                    Love this recipe?
                  </Typography>
                </Box>
                {showAppBenefits && (
                  <IconButton size="small" onClick={() => setShowAppBenefits(false)}>
                    <Close fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" paragraph>
                Get organized with Earn-E - save recipes, plan meals, manage tasks, and earn allowances all in one place.
              </Typography>

              <Collapse in={!showAppBenefits}>
                <Button
                  fullWidth
                  variant="text"
                  endIcon={<ExpandMore />}
                  onClick={() => setShowAppBenefits(true)}
                  sx={{ mb: 2 }}
                >
                  See what you get
                </Button>
              </Collapse>

              <Collapse in={showAppBenefits}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    ✨ <strong>Save unlimited recipes</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    🛒 <strong>Auto-generate shopping lists</strong> from recipes
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    📸 <strong>Scan recipes</strong> from photos
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    🤖 <strong>AI cooking assistant</strong> (Burt)
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    👨‍👩‍👧‍👦 <strong>Share with family</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    💰 <strong>Manage family tasks & allowances</strong>
                  </Typography>
                </Box>
              </Collapse>

              {!currentUser ? (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/auth?ref=recipe')}
                  sx={{ mb: 1 }}
                >
                  Try Earn-E Free
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/parent')}
                  sx={{ mb: 1 }}
                >
                  Go to My Recipes
                </Button>
              )}

              <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                No credit card required • Free plan available
              </Typography>

              <Divider sx={{ my: 3 }} />

              {/* Quick Actions */}
              <Typography variant="subtitle2" gutterBottom>
                Quick Actions
              </Typography>
              
              {!currentUser && (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => navigate('/auth?action=save-recipe&recipeId=' + recipeId)}
                  size="small"
                  sx={{ mb: 1 }}
                >
                  Save to My Collection
                </Button>
              )}

              <Button
                fullWidth
                variant="outlined"
                startIcon={<Share />}
                onClick={handleShare}
                size="small"
              >
                Share with Friends
              </Button>
            </Paper>
          </Grid>
        </Grid>

        {/* Bottom CTA - Super Subtle */}
        {!currentUser && (
          <Card
            sx={{
              mt: 4,
              p: 3,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
            className="no-print"
          >
            <Typography variant="h5" gutterBottom>
              Ready to get organized?
            </Typography>
            <Typography variant="body1" paragraph>
              Join families using Earn-E to plan meals, manage tasks, and teach financial responsibility.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/auth')}
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' }
              }}
            >
              Get Started Free
            </Button>
          </Card>
        )}
      </Container>

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </Box>
  );
}

